interface IntersectionObserverOptions {
  rootMargin?: string;
  threshold?: number | number[];
  root?: Element | null;
}

interface PrefetchOptions {
  selector: string;
  hrefPrefix?: string;
  rootMargin?: string;
  threshold?: number | number[];
  root?: Element | null;
  onIntersect?: (element: Element, href: string) => void;
  stopObservingAfterIntersect?: boolean;
}

interface PrefetchConfig {
  selector: string;
  hrefPrefix?: string;
  enableHover?: boolean;
  enableIntersection?: boolean;
  rootMargin?: string;
  threshold?: number | number[];
  root?: Element | null;
  onIntersect?: (element: Element, href: string) => void;
  stopObservingAfterIntersect?: boolean;
}

/**
 * Prefetches a link by creating a link element with prefetch rel
 */
function prefetchLink(href: string): void {
  const prefetchLink = document.createElement('link');
  prefetchLink.rel = 'prefetch';
  prefetchLink.href = href;
  document.head.appendChild(prefetchLink);
}

/**
 * Sets up hover prefetching for links
 */
function setupHoverPrefetching(selector: string, hrefPrefix: string = '/posts/'): void {
  document.querySelectorAll(selector).forEach(link => {
    link.addEventListener('mouseenter', () => {
      const href = link.getAttribute('href');
      if (href && href.startsWith(hrefPrefix)) {
        prefetchLink(href);
      }
    });
  });
}

/**
 * Creates a reusable IntersectionObserver for prefetching links
 */
export function createPrefetchObserver(options: PrefetchOptions) {
  const {
    selector,
    hrefPrefix = '/posts/',
    rootMargin = '50px',
    threshold = 0,
    root = null,
    onIntersect,
    stopObservingAfterIntersect = true
  } = options;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const element = entry.target;
        const href = element.getAttribute('href');
        
        if (href && href.startsWith(hrefPrefix)) {
          // Prefetch the link
          prefetchLink(href);
          
          // Call custom callback if provided
          if (onIntersect) {
            onIntersect(element, href);
          }
          
          // Stop observing if configured to do so
          if (stopObservingAfterIntersect) {
            observer.unobserve(element);
          }
        }
      }
    });
  }, {
    rootMargin,
    threshold,
    root
  });

  return observer;
}

/**
 * Sets up prefetching for links that match a selector
 */
export function setupPrefetching(options: PrefetchOptions) {
  const observer = createPrefetchObserver(options);
  
  // Observe all matching elements
  document.querySelectorAll(options.selector).forEach(element => {
    observer.observe(element);
  });

  return observer;
}

/**
 * Comprehensive prefetch setup that handles both hover and intersection-based prefetching
 */
export function setupComprehensivePrefetching(config: PrefetchConfig) {
  const {
    selector,
    hrefPrefix = '/posts/',
    enableHover = true,
    enableIntersection = true,
    rootMargin = '50px',
    threshold = 0,
    root = null,
    onIntersect,
    stopObservingAfterIntersect = true
  } = config;

  let observer: IntersectionObserver | null = null;

  // Setup hover prefetching
  if (enableHover) {
    setupHoverPrefetching(selector, hrefPrefix);
  }

  // Setup intersection observer prefetching
  if (enableIntersection) {
    observer = createPrefetchObserver({
      selector,
      hrefPrefix,
      rootMargin,
      threshold,
      root,
      onIntersect,
      stopObservingAfterIntersect
    });

    document.querySelectorAll(selector).forEach(element => {
      observer!.observe(element);
    });
  }

  return observer;
}

/**
 * Generic IntersectionObserver utility for any intersection-based functionality
 */
export function createIntersectionObserver(
  callback: (entries: IntersectionObserverEntry[]) => void,
  options: IntersectionObserverOptions = {}
) {
  const {
    rootMargin = '0px',
    threshold = 0,
    root = null
  } = options;

  return new IntersectionObserver(callback, {
    rootMargin,
    threshold,
    root
  });
}

/**
 * Observe elements with a custom callback
 */
export function observeElements(
  selector: string,
  callback: (entry: IntersectionObserverEntry) => void,
  options: IntersectionObserverOptions = {}
) {
  const observer = createIntersectionObserver((entries) => {
    entries.forEach(callback);
  }, options);

  document.querySelectorAll(selector).forEach(element => {
    observer.observe(element);
  });

  return observer;
} 